/**
 * My access cases (5 Oct): every person whose access the reader asked for,
 * handles, or is named on — New and Change, one tile each.
 *
 * "New starters" was merged in here on 5 Oct (product owner): one list, a
 * type badge on each tile and a filter (All / New / Change, `?type=`). New
 * starter cases the front door did not raise come from the starters engine
 * (for those who see them, `has_starter_cases`) and keep their own page;
 * each source applies its own visibility rules. `/starters` lands on
 * `?type=new`. The menu entry opens the wizard; this list is linked from it.
 *
 * WHY (5 Oct, product owner's UAT): "the tiles should be smaller and show what
 * stage it is in … complete or closed should appear only in an archive, which
 * makes it easy for auditing". Compact tiles in three sections — Needs your
 * action, In progress, and the Archive (collapsed) — with a search box by
 * person, request number, system or approver beside the type filter.
 *
 * 6 Oct (decision 34): a "Completed" tab beside the list (`?view=completed`),
 * for auditors — admins and IT only (`useMe().access_audit`); see
 * `components/access-completed.tsx`.
 *
 * 19 Oct (target New user flow): two work queues beside them, for the same
 * readers: "Global IT" (`?view=global-it`) and "Backoffice"
 * (`?view=backoffice`); see `components/access-queues.tsx`. The tabs show
 * their counts, and a member of Global IT Helpdesk or the Backoffice Team
 * lands on their own queue (UI review, 19 Oct).
 */

import { Icon, KeyRound, Search } from "@norican/icons";
import { Badge, Button, Collapsible, Input, Tabs, Typography } from "@norican/react";
import { type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  EmptyStateBlock,
  ErrorState,
  LoadingRows,
  PageContainer,
  ResultStateNotice,
} from "@/components/common";
import { StarterCard, useStarterCases } from "@/features/starters";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { useMe } from "@/lib/api";

import { useAccessCases, useAccessQueues } from "./api";
import { AccessCaseCard } from "./components/access-case-card";
import { AccessCompletedPanel } from "./components/access-completed";
import { AccessQueuesPanel } from "./components/access-queues";
import { useWaitingWords } from "./components/waiting-on";
import {
  accessSearchText,
  type CaseFilter,
  matchesSearch,
  mergeCaseList,
  parseCaseFilter,
  sectionRows,
} from "./lib/access";

const FILTERS: readonly CaseFilter[] = ["all", "new", "change", "leave"];

/** The auditors' tabs (6 Oct; the two queues 19 Oct). */
const VIEWS = ["list", "global-it", "backoffice", "completed"] as const;
type View = (typeof VIEWS)[number];

function parseView(value: string | null): View {
  return VIEWS.find((view) => view === value) ?? "list";
}

export function AccessCasesPage() {
  const { t } = useT("tickets");
  useDocumentTitle(t("access.title"));
  const [params, setParams] = useSearchParams();
  const { data: me } = useMe();
  const auditor = me?.access_audit === true;
  // 19 Oct (UI review, finding 9): the queue tabs carry their counts, and the
  // page opens on the reader's own team's queue (Global IT or Backoffice)
  // unless the link names a view. Same query as the queue panel (one cache).
  const queues = useAccessQueues(auditor);
  const myTeam = queues.data?.my_team ?? null;
  const asked = params.get("view");
  const view: View = auditor ? (asked === null && myTeam ? myTeam : parseView(asked)) : "list";
  const globalItCount = queues.data?.global_it?.length ?? null;
  const backofficeCount =
    queues.data === undefined
      ? null
      : (queues.data.roles_to_select?.length ?? 0) + (queues.data.ready_to_assign?.length ?? 0);

  return (
    <PageContainer
      title={t("access.title")}
      subtitle={t("access.subtitle")}
      actions={<Button render={<Link to="/access/new" />}>{t("access.wizard.title")}</Button>}
    >
      <div className="flex flex-col gap-5">
        {auditor ? (
          <Tabs.Root
            value={view}
            onValueChange={(value) => {
              const next = new URLSearchParams(params);
              const chosen = parseView(String(value));
              // "list" is named when the page would otherwise open on a team queue.
              if (chosen === "list" && myTeam === null) {
                next.delete("view");
              } else {
                next.set("view", chosen);
              }
              setParams(next, { replace: true });
            }}
          >
            <Tabs.List aria-label={t("access.views.label")}>
              <Tabs.Tab value="list">{t("access.views.list")}</Tabs.Tab>
              <Tabs.Tab value="global-it">
                {globalItCount === null
                  ? t("access.views.globalIt")
                  : t("access.views.globalItCount", { n: globalItCount })}
              </Tabs.Tab>
              <Tabs.Tab value="backoffice">
                {backofficeCount === null
                  ? t("access.views.backoffice")
                  : t("access.views.backofficeCount", { n: backofficeCount })}
              </Tabs.Tab>
              <Tabs.Tab value="completed">{t("access.views.completed")}</Tabs.Tab>
              <Tabs.Indicator />
            </Tabs.List>
          </Tabs.Root>
        ) : null}
        {view === "completed" ? (
          <AccessCompletedPanel />
        ) : view === "global-it" || view === "backoffice" ? (
          <AccessQueuesPanel view={view} />
        ) : (
          <AccessCasesList />
        )}
      </div>
    </PageContainer>
  );
}

/** The list itself: Needs your action, In progress, and the Archive. */
function AccessCasesList() {
  const { t } = useT("tickets");
  const [params, setParams] = useSearchParams();
  const filter = parseCaseFilter(params.get("type"));
  const [search, setSearch] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const words = useWaitingWords();
  const { data: me } = useMe();
  const query = useAccessCases();
  // WHY (5 Oct): not on `has_starter_cases` alone — it is false while a cold
  // case list outlasts its timeout, which hid every New case here (and
  // `/starters` lands here). The list itself returns only what one may see.
  // 28 Oct (Rusty): on the new forms, no old-engine New User Request rows.
  const startersOn =
    me?.access_new_forms !== true &&
    (me?.has_starter_cases === true || me?.access_front_door === true);
  const starters = useStarterCases({ enabled: startersOn });
  const rows = mergeCaseList(
    query.data?.items ?? [],
    startersOn ? (starters.data?.items ?? []) : [],
    filter,
  ).filter((row) =>
    matchesSearch(
      row.kind === "access"
        ? accessSearchText(row.item, words.shortName)
        : [
            row.item.new_user_name,
            row.item.nur_number,
            row.item.manager?.display_name ?? "",
            row.item.job_title ?? "",
          ].join(" "),
      search,
    ),
  );
  const sections = sectionRows(rows);
  const attention = query.data?.attention_count ?? 0;
  const searching = search.trim() !== "";

  const renderRows = (list: typeof rows) => (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {list.map((row) => (
        <li key={`${row.kind}-${row.id}`}>
          {row.kind === "access" ? (
            <AccessCaseCard item={row.item} />
          ) : (
            <StarterCard
              item={row.item}
              typeBadge={
                <Badge tone="accent" variant="subtle" size="xs">
                  {t("access.type.new")}
                </Badge>
              }
            />
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          size="sm"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value.slice(0, 200))}
          placeholder={t("access.search.placeholder")}
          aria-label={t("access.search.label")}
          startSlot={<Icon icon={Search} size="sm" />}
          className="min-w-0 grow basis-full sm:basis-64"
        />
        <Tabs.Root
          value={filter}
          onValueChange={(value) => {
            const next = new URLSearchParams(params);
            if (value === "all") {
              next.delete("type");
            } else {
              next.set("type", String(value));
            }
            setParams(next, { replace: true });
          }}
        >
          <Tabs.List aria-label={t("access.filter.label")}>
            {FILTERS.map((key) => (
              <Tabs.Tab key={key} value={key}>
                {t(`access.filter.${key}`)}
              </Tabs.Tab>
            ))}
            <Tabs.Indicator />
          </Tabs.List>
        </Tabs.Root>
      </div>
      <ResultStateNotice result={query.data?.result} />
      {attention > 0 ? (
        <Typography as="p" variant="body-sm" className="text-fg font-medium">
          {t("access.needAttention", { count: attention })}
        </Typography>
      ) : null}
      {query.isPending ? (
        <LoadingRows rows={3} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        searching ? (
          <Typography as="p" variant="body-sm" tone="muted">
            {t("access.search.empty")}
          </Typography>
        ) : (
          <EmptyStateBlock
            icon={<Icon icon={KeyRound} size="lg" tone="accent" />}
            title={t("access.empty.title")}
            description={t("access.empty.description")}
          />
        )
      ) : (
        <div className="flex flex-col gap-6" aria-label={t("access.listLabel")}>
          {sections.action.length > 0 ? (
            <Section title={t("access.sections.action")} count={sections.action.length}>
              {renderRows(sections.action)}
            </Section>
          ) : null}
          {sections.progress.length > 0 ? (
            <Section title={t("access.sections.progress")} count={sections.progress.length}>
              {renderRows(sections.progress)}
            </Section>
          ) : null}
          {sections.archive.length > 0 ? (
            <Collapsible.Root
              // A search opens the archive: finding an approval is what it is for.
              open={archiveOpen || searching}
              onOpenChange={setArchiveOpen}
              className="border-border flex flex-col gap-3 rounded-lg border px-3 py-2"
              render={<section aria-label={t("access.sections.archive")} />}
            >
              <Collapsible.Trigger className="hover:text-accent-fg-subtle ease-standard rounded-md transition-colors">
                <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
                  <span className="flex items-center gap-2">
                    <Typography as="span" variant="heading-6">
                      {t("access.sections.archive")}
                    </Typography>
                    <Badge tone="neutral" variant="subtle" size="xs">
                      {sections.archive.length}
                    </Badge>
                  </span>
                  <Typography as="span" variant="caption" tone="muted">
                    {t("access.sections.archiveHint")}
                  </Typography>
                </span>
              </Collapsible.Trigger>
              <Collapsible.Panel>{renderRows(sections.archive)}</Collapsible.Panel>
            </Collapsible.Root>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** One section of the list: a heading with its count, then its tiles. */
function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <span className="flex items-center gap-2">
        <Typography as="h2" variant="heading-6">
          {title}
        </Typography>
        <Badge tone="neutral" variant="subtle" size="xs">
          {count}
        </Badge>
      </span>
      {children}
    </section>
  );
}
